import classNames from 'classnames';
import PropTypes from 'prop-types';

export interface InputProps {
  /**
   * An alias for value only when value is a boolean. Provided for
   * convenience of destructuring the whole field object into the props of a
   * form element.
   */
  checked?: boolean;

  /**
   * The name prop passed in.
   */
  name: string;

  /**
   * The value of this form field. It will be a boolean for checkboxes, and
   * a string for all other input types. If there is no value in the Redux
   * state for this field, it will default to ''. This is to
   * ensure that the input is controlled.
   */
  value: FieldValue;

  onChange: (value: FieldValue) => void;
}

export type FieldValue = any;

/**
 * These props are metadata about the state of this field that redux-form is tracking for you.
 */
export interface MetaProps {
  /**
   * true if this field currently has focus. It will only work if you are
   * passing onFocus to your input element.
   */
  active?: boolean;

  /**
   * true if this field has been set with the AUTOFILL action and has not since been changed
   * with a CHANGE action. This is useful to render the field in a way that the user can tell
   * that the value was autofilled for them.
   */
  autofilled?: boolean;

  /**
   * true if the form is currently running asynchronous validation because this
   * field was blurred.
   */
  asyncValidating?: boolean;

  /**
   * true if the field value has changed from its initialized value.
   * Opposite of pristine.
   */
  dirty?: boolean;

  /**
   * The error for this field if its value is not passing validation. Both
   * synchronous, asynchronous, and submit validation errors will be reported here.
   */
  error?: string;

  /**
   * The name of the form. Could be useful if you want to manually dispatch actions.
   */
  form?: string;

  /**
   * true if the field value fails validation (has a validation error).
   * Opposite of valid.
   */
  invalid?: boolean;

  /**
   * true if the field value is the same as its initialized value. Opposite
   * of dirty.
   */
  pristine?: boolean;

  /**
   * true if the field is currently being submitted
   */
  submitting?: boolean;

  /**
   * true if the form had onSubmit called and failed to submit for any reason.
   * A subsequent successful submit will set it back to false.
   */
  submitFailed?: boolean;

  /**
   * true if the field has been touched. By default this will be set when
   * the field is blurred.
   */
  touched?: boolean;

  /**
   * true if the field value passes validation (has no validation errors).
   * Opposite of invalid.
   */
  valid?: boolean;

  /**
   * true if this field has ever had focus. It will only work if you are
   * passing onFocus to your input element.
   */
  visited?: boolean;

  /**
   * The warning for this field if its value is not passing warning validation.
   */
  warning?: string;
}

export interface FieldProps {
  input: InputProps;
  meta: MetaProps;
  styles: Styles;
}

// based on https://github.com/erikras/redux-form/blob/master/docs/api/Field.md#props
export const fieldPropTypes = {
  input: PropTypes.shape({
    checked: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onDragStart: PropTypes.func.isRequired,
    onDrop: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    value: PropTypes.any.isRequired,
  }),
  meta: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    autofilled: PropTypes.bool.isRequired,
    asyncValidating: PropTypes.bool.isRequired,
    dirty: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    error: PropTypes.string,
    invalid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    submitFailed: PropTypes.bool,
    touched: PropTypes.bool.isRequired,
    valid: PropTypes.bool.isRequired,
    visited: PropTypes.bool.isRequired,
    warning: PropTypes.string,
  }),
  styles: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string),
  ]),
};

// Delete the below when DNA has been fully removed from the project
export function inputDNAClassNames(
  { touched, error }: { touched: boolean; error?: string },
  mainClass = 'dna-input',
  ...additionalClasses: string[]
) {
  return classNames(
    mainClass,
    { 'dna-input-error': !!touched && !!error },
    ...additionalClasses
  );
}

export function inputClassNames(
  { touched, error }: { touched?: boolean; error?: string },
  mainClass = 'jg-form-control',
  ...additionalClasses: string[]
) {
  return classNames(
    mainClass,
    { 'jg-form-control--error': !!touched && !!error },
    ...additionalClasses
  );
}

export function focusErrorInput(form: HTMLFormElement) {
  if (__CLIENT__) {
    const input = form.querySelector(
      '.dna-input-error'
    ) as HTMLInputElement | null;

    if (input) {
      input.focus();
    }
  }
}



// WEBPACK FOOTER //
// ./src/helpers/forms.ts